import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges
} from '@angular/core';
import {
    dreamTeamsRoutePath,
    dreamTeamsSelfServeRoutePath
} from '../constants-types/Route.constants';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { TranslationService } from '../translations/Translation.service';
import { NavigationItemOptions } from './navigation-item.component';

export enum ProductNavigationMenu {
    Unknown = 0,
    Browse = 1,
    Design = 2,
    Hire = 3,
    Inspire = 4,
    Diagnose = 5
}

@Component({
    selector: 'mini-navigation-dropdown',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ul class="piNavMenu--subMenu subMenu--browse">
            @for (option of navOptions; track option) {
                <li
                    [piNavigationItem]="option"
                    [isActive]="moduleIsActive"
                ></li>
            }
        </ul>
    `,
    standalone: false
})
export class MiniNavigationDropdownComponent implements OnChanges {
    @Input()
    moduleIsActive = true;
    @Input()
    productNavigationMenu: ProductNavigationMenu;
    @Input()
    isReadOnly: boolean;
    @Input()
    isOrganizationEnabled: boolean;

    navOptions: NavigationItemOptions[];

    constructor(
        private TR: TranslationService,
        private moduleAccess: ModuleAccessService
    ) {}

    ngOnChanges() {
        switch (this.productNavigationMenu) {
            case ProductNavigationMenu.Browse:
                this.navOptions = this.generateBrowseNavOptions();
                break;
            case ProductNavigationMenu.Design:
                this.navOptions = this.generateDesignNavOptions();
                break;
            case ProductNavigationMenu.Hire:
                this.navOptions = this.generateHireNavOptions();
                break;
            case ProductNavigationMenu.Inspire:
                this.navOptions = this.generateInspireNavOptions();
                break;
            case ProductNavigationMenu.Diagnose:
                this.navOptions = this.generateDiagnoseNavOptions();
                break;
            default:
                break;
        }
    }

    private generateBrowseNavOptions(): NavigationItemOptions[] {
        const orgLink = '/organization';
        const assessmentLink = '/assessmentCenter';
        const foldersLink = '/folders';
        const teamsLink = '/groups';
        const analyticsLink = '/analyzeGroups/select';

        const nonSelfServeOptions = [
            orgLink,
            assessmentLink,
            foldersLink,
            teamsLink,
            analyticsLink
        ];

        let browseNavOptions = [
            {
                routerLink: '/dashboard',
                text: this.TR.key('label.common-terms.dashboard')
            },
            {
                routerLink: assessmentLink,
                text: this.TR.key('common.assessmentCenter')
            },
            {
                routerLink: foldersLink,
                text: this.TR.key('label.common-terms.folders')
            },
            {
                routerLink: teamsLink,
                text: this.TR.key('label.common-terms.teams')
            },
            {
                routerLink: orgLink,
                text: this.TR.key('navigation.organization')
            }
        ];

        if (!this.isOrganizationEnabled) {
            browseNavOptions = browseNavOptions.filter(
                (option) => option.routerLink !== orgLink
            );
        }

        if (this.isReadOnly) {
            browseNavOptions = browseNavOptions.filter(
                (option) => option.routerLink !== assessmentLink
            );
        }

        browseNavOptions = browseNavOptions.concat([
            {
                text: this.TR.key('navigation.employeeDirectory'),
                routerLink: '/employeeDirectory'
            },
            {
                text: this.TR.key('common.analytics'),
                routerLink: analyticsLink
            }
        ]);

        // Removing menu options for Self Serve based modules
        if (this.moduleAccess.hasAnySelfServeModule) {
            browseNavOptions = browseNavOptions.filter(
                (navOptions) =>
                    !nonSelfServeOptions.includes(navOptions.routerLink)
            );
        }

        return browseNavOptions;
    }

    private generateDesignNavOptions(): NavigationItemOptions[] {
        const dreamTeamsRoute = this.moduleAccess.isTeamDiscoverySelfServe
            ? `/design/${dreamTeamsSelfServeRoutePath}`
            : `/design/${dreamTeamsRoutePath}`;

        return [
            {
                text: this.TR.key('design.team-discovery.title'),
                routerLink: dreamTeamsRoute
            }
        ];
    }

    private generateHireNavOptions(): NavigationItemOptions[] {
        let hireNavOptions = [
            {
                text: this.TR.key('navigation.review-candidate'),
                routerLink: '/assessCandidates/review'
            },
            {
                text: this.TR.key('navigation.interview-candidates'),
                routerLink: '/interviewCandidates'
            },
            {
                text: this.TR.key('navigation.onboard-new-employees'),
                routerLink: '/onboardNewEmployees'
            }
        ];

        if (!this.isReadOnly) {
            hireNavOptions = [
                {
                    text: this.TR.key('navigation.create-job'),
                    routerLink: '/createJob'
                },
                {
                    text: this.TR.key('navigation.finalize-job-target'),
                    routerLink: '/setJobTarget'
                },
                {
                    text: this.TR.key('navigation.assess-candidate'),
                    routerLink: '/assessmentCenter'
                }
            ].concat(hireNavOptions);
        }
        return hireNavOptions;
    }

    private generateInspireNavOptions(): NavigationItemOptions[] {
        const inspireNavOptions = [
            {
                text: this.TR.key('navigation.individualsMenu'),
                routerLink: '/inspire/individuals'
            },
            {
                text: this.TR.key('navigation.managersMenu'),
                routerLink: '/inspire/managers'
            }
        ];

        return inspireNavOptions;
    }

    private generateDiagnoseNavOptions(): NavigationItemOptions[] {
        return [
            {
                text: this.TR.key('navigation-engage.employeeExperience'),
                routerLink: '/engage/employeeExperience'
            }
        ];
    }
}
